import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Modal,
  Progress,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeFilled,
  TeamOutlined,
  WarningFilled,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import ViolationBadge from "../../../components/ViolationBadge";

const violationColor = (n) =>
  n === 0 ? "#52c41a" : n <= 5 ? "#fa8c16" : "#f5222d";

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const avatarColor = (name = "") => {
  const colors = ["#1890ff", "#722ed1", "#13c2c2", "#fa8c16", "#eb2f96", "#52c41a"];
  return colors[name.charCodeAt(0) % colors.length];
};

const StatCard = ({ label, value, color, icon, sub }) => (
  <Card
    style={{ borderRadius: 14, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", height: "100%" }}
    styles={{ body: { padding: "18px 20px" } }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  </Card>
);

const HoursBar = ({ rest, work }) => {
  const total = rest + work || 1;
  const restPct = Math.round((rest / total) * 100);
  return (
    <div style={{ fontSize: 11, color: "#888" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ color: "#52c41a" }}>Rest {Number(rest).toFixed(0)}h</span>
        <span style={{ color: "#1890ff" }}>Work {Number(work).toFixed(0)}h</span>
      </div>
      <div style={{ display: "flex", borderRadius: 4, overflow: "hidden", height: 6, background: "#1890ff" }}>
        <div style={{ width: `${restPct}%`, background: "#52c41a" }} />
      </div>
    </div>
  );
};

const CrewCard = ({ member, onView, onViewViolations }) => {
  const hasRecord = !!member.record_status;
  return (
    <Card
      style={{
        borderRadius: 14,
        border: `1.5px solid ${member.is_compliant ? "#b7eb8f" : hasRecord ? "#ffccc7" : "#e8e8e8"}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        height: "100%",
      }}
      styles={{ body: { padding: "16px" } }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Avatar
            size={40}
            style={{ background: avatarColor(member.staff_name), fontWeight: 600, flexShrink: 0 }}
          >
            {initials(member.staff_name)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 120,
              }}
              title={member.staff_name}
            >
              {member.staff_name}
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>{member.role}</div>
          </div>
        </div>
        {member.is_compliant ? (
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: hasRecord ? "#f5222d" : "#d9d9d9", fontSize: 18 }} />
        )}
      </div>

      <div style={{ margin: "12px 0 8px" }}>
        {hasRecord ? (
          <HoursBar rest={member.total_rest_hours} work={member.total_work_hours} />
        ) : (
          <div
            style={{
              textAlign: "center",
              fontSize: 11,
              color: "#bbb",
              background: "#fafafa",
              borderRadius: 6,
              padding: "6px 0",
            }}
          >
            No record this month
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {member.total_violations > 0 && (
            <Tooltip title="Click to view violation details">
              <Badge
                count={member.total_violations}
                style={{ backgroundColor: violationColor(member.total_violations), fontSize: 10, cursor: "pointer" }}
                onClick={onViewViolations}
              />
            </Tooltip>
          )}
          {member.record_status && (
            <Tag
              color={member.record_status === "closed" ? "green" : "orange"}
              style={{ fontSize: 10, padding: "0 5px", margin: 0 }}
            >
              {member.record_status}
            </Tag>
          )}
        </div>
        <Tooltip title="View Monthly Record">
          <Button
            type="text"
            size="small"
            icon={<EyeFilled />}
            style={{ color: "#1890ff" }}
            onClick={onView}
          />
        </Tooltip>
      </div>
    </Card>
  );
};

const RestHoursDashboardCom = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [ships, setShips] = useState([]);
  const [shipId, setShipId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const [violModal, setViolModal]   = useState({ open: false, member: null });
  const [violRecord, setViolRecord] = useState(null);
  const [violLoading, setViolLoading] = useState(false);

  const openViolations = (member) => {
    setViolModal({ open: true, member });
    setViolRecord(null);
    setViolLoading(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("staff_id", member.staff_id);
    FORM_DATA.append("year", data.year);
    FORM_DATA.append("month", data.month);
    post(ApiURL.view_rest_hours_record_com, FORM_DATA).then((res) => {
      setViolRecord(res?.status === true ? res.data || null : null);
      setViolLoading(false);
    });
  };

  useEffect(() => {
    const FORM_DATA = new FormData();
    post(ApiURL.list_ships_view_com, FORM_DATA).then((res) => {
      if (res?.status === true) {
        const list = res.ships || [];
        setShips(list);
        if (list.length > 0) {
          setShipId(list[0].id);
          fetchDashboard(list[0].id, dayjs());
        }
      }
    });
  }, []);

  const fetchDashboard = (sid, month) => {
    if (!sid) return;
    setLoading(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("ship_id", sid);
    FORM_DATA.append("year", month.format("YYYY"));
    FORM_DATA.append("month", month.format("M"));
    post(ApiURL.rest_hours_dashboard_com, FORM_DATA).then((res) => {
      if (res?.status === true) setData(res.data || null);
      else setData(null);
      setLoading(false);
    });
  };

  const crew = data?.crew || [];
  const totalViolations = crew.reduce((s, m) => s + (m.total_violations || 0), 0);
  const compliantCount = crew.filter((m) => m.is_compliant).length;
  const nonCompliantCount = crew.length - compliantCount;
  const compliancePct = crew.length ? Math.round((compliantCount / crew.length) * 100) : 0;
  const period = data ? dayjs(`${data.year}-${data.month}-01`).format("MMMM YYYY") : "";

  const goToRecord = (staffId) =>
    navigate("/resthours/crew-records", {
      state: { staff_id: staffId, year: data?.year, month: data?.month },
    });

  return (
    <>
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
          borderRadius: 16,
          padding: "20px 24px",
          marginBottom: 16,
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ color: "#ffffff99", fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
            Rest Hours Compliance
          </div>
          <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
            {data?.ship_name || "Select a ship"} — {period || "Loading…"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            placeholder="Select Ship"
            value={shipId}
            style={{ width: 180, borderRadius: 8 }}
            onChange={(val) => setShipId(val)}
          >
            {ships.map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.ship_name}
              </Select.Option>
            ))}
          </Select>
          <DatePicker
            picker="month"
            value={selectedMonth}
            format="MMM YYYY"
            style={{ width: 130, borderRadius: 8 }}
            disabledDate={(c) => c && c > dayjs().endOf("month")}
            onChange={(val) => { if (val) setSelectedMonth(val); }}
          />
          <Button
            type="primary"
            loading={loading}
            style={{ borderRadius: 8, background: "#1890ff" }}
            onClick={() => fetchDashboard(shipId, selectedMonth)}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Summary stat cards */}
      {crew.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={12} md={6}>
            <StatCard label="Total Crew" value={crew.length} color="#1890ff" icon={<TeamOutlined />} sub={period} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard label="Compliant" value={compliantCount} color="#52c41a" icon={<CheckCircleFilled />} sub={`${compliancePct}% of crew`} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Non-Compliant"
              value={nonCompliantCount}
              color={nonCompliantCount > 0 ? "#f5222d" : "#52c41a"}
              icon={<CloseCircleFilled />}
              sub={nonCompliantCount > 0 ? "Action required" : "All clear"}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Crew Violations"
              value={totalViolations}
              color={violationColor(totalViolations)}
              icon={<WarningFilled />}
              sub="Total across all crew"
            />
          </Col>
        </Row>
      )}

      {/* Compliance ring + crew cards */}
      {crew.length > 0 && (
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={24} md={6}>
            <Card
              style={{ borderRadius: 14, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", height: "100%", minHeight: 200 }}
              styles={{
                body: {
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                },
              }}
            >
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Crew Compliance Rate
              </div>
              <Progress
                type="circle"
                size={130}
                percent={compliancePct}
                strokeColor={compliancePct === 100 ? "#52c41a" : compliancePct >= 60 ? "#fa8c16" : "#f5222d"}
                format={(p) => (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{p}%</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Compliant</div>
                  </div>
                )}
              />
              <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#52c41a" }}>{compliantCount}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>OK</div>
                </div>
                <div style={{ width: 1, background: "#f0f0f0" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#f5222d" }}>{nonCompliantCount}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>Issues</div>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={24} md={18}>
            <Row gutter={[10, 10]}>
              {crew.map((member) => (
                <Col key={member.staff_id} xs={24} sm={12} md={12} lg={8}>
                  <CrewCard
                    member={member}
                    onView={() => goToRecord(member.staff_id)}
                    onViewViolations={() => openViolations(member)}
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {crew.length === 0 && !loading && (
        <Card
          style={{ borderRadius: 14, textAlign: "center", marginTop: 16, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        >
          <UserOutlined style={{ fontSize: 40, color: "#d9d9d9", marginBottom: 12 }} />
          <div style={{ color: "#aaa" }}>No crew data found for this ship / month.</div>
        </Card>
      )}

      {/* Per-staff violations modal */}
      <Modal
        open={violModal.open}
        onCancel={() => setViolModal({ open: false, member: null })}
        footer={null}
        title={
          <span>
            STCW Violations —{" "}
            <strong>{violModal.member?.staff_name}</strong>
            <span style={{ color: "#888", fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
              {data ? dayjs(`${data.year}-${data.month}-01`).format("MMMM YYYY") : ""}
            </span>
          </span>
        }
        width={580}
        styles={{ body: { maxHeight: "65vh", overflowY: "auto", paddingTop: 12 } }}
      >
        {violLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : (() => {
          const allViolations = (violRecord?.days || []).flatMap((d) =>
            (d.violations || []).map((v) => ({ ...v, date: d.date }))
          );
          if (!allViolations.length) return <Empty description="No violations this month" />;
          const byDate = allViolations.reduce((acc, v) => {
            (acc[v.date] = acc[v.date] || []).push(v);
            return acc;
          }, {});
          return Object.entries(byDate)
            .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix())
            .map(([date, viols]) => (
              <div key={date} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, color: "#555", fontSize: 12, marginBottom: 6, borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}>
                  {dayjs(date).format("DD MMM YYYY")}
                </div>
                {viols.map((v, i) => <ViolationBadge key={i} rule={v.rule} detail={v.detail} />)}
              </div>
            ));
        })()}
      </Modal>
    </>
  );
};

export default RestHoursDashboardCom;
